import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { protectedResources } from '../auth-config';
import { Observable, of, map } from 'rxjs';

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class ClusterService {
  url = protectedResources.apiClusters.endpoint;

  constructor(private httpClient: HttpClient) {}

  private normalizeCluster(item: any): any {
    return {
      ...item,
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name,
      externalId: item?.externalId ?? item?.ExternalId,
      cloudProvider: item?.cloudProvider ?? item?.CloudProvider,
      department: item?.department ?? item?.Department,
      departmentSource: item?.departmentSource ?? item?.DepartmentSource,
      sourceConnectors: item?.sourceConnectors ?? item?.SourceConnectors ?? [],
    };
  }

  private extractItems<T>(response: any): T[] {
    if (Array.isArray(response)) {
      return response as T[];
    }

    const source = response?.data ?? response?.result ?? response;
    const items = source?.items ?? source?.Items ?? source?.value ?? [];

    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item: any) => this.normalizeCluster(item)) as T[];
  }

  private normalizePaginatedResponse<T>(response: any): PaginatedResponse<T> {
    const source = response?.data ?? response?.result ?? response ?? {};
    const items = this.extractItems<T>(source);

    return {
      items,
      totalCount: Number(source.totalCount ?? source.TotalCount ?? items.length ?? 0),
      pageNumber: Number(source.pageNumber ?? source.PageNumber ?? 0),
      pageSize: Number(source.pageSize ?? source.PageSize ?? items.length ?? 0),
    };
  }

  /**
   * Search clusters by ID or name (server-side search)
   * This is the preferred method for users with many clusters
   * @param searchTerm - Cluster ID or name to search for
   */
  public searchClusters(searchTerm: string): Observable<any[]> {
    const params = new HttpParams().set('search', searchTerm.trim());
    return this.httpClient
      .get<any>(this.url, { params })
      .pipe(map((response) => this.extractItems<any>(response)));
  }

  /**
   * Get a specific cluster by external ID
   * @param externalId - The cluster ID
   */
  public getClusterById(externalId: string): Observable<any> {
    return this.httpClient.get<any>(`${this.url}/${externalId}`);
  }

  /**
   * Get paginated clusters
   * Use this for browsing when search isn't available
   * @param pageNumber - Page number (0-based)
   * @param pageSize - Number of items per page
   * @param searchTerm - Optional search term
   */
  public getClustersPaginated(
    pageNumber: number = 0,
    pageSize: number = 50,
    searchTerm?: string
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    
    if (searchTerm && searchTerm.trim()) {
      params = params.set('search', searchTerm.trim());
    }

    return this.httpClient
      .get<any>(this.url, { params })
      .pipe(map((response) => this.normalizePaginatedResponse<any>(response)));
  }

  /**
   * Get clusters by IDs
   * Useful for loading only specific clusters (e.g., already selected ones)
   * @param externalIds - Array of cluster IDs
   */
  public getClustersByIds(externalIds: string[]): Observable<any[]> {
    if (!externalIds || externalIds.length === 0) {
      return of([]);
    }

    const params = new HttpParams().set('ids', externalIds.join(','));
    return this.httpClient
      .get<any>(this.url, { params })
      .pipe(map((response) => this.extractItems<any>(response)));
  }

  /**
   * @deprecated Use searchClusters or getClustersPaginated instead
   * This method loads ALL clusters and should be avoided for users with many clusters
   */
  public getClusters(): Observable<any[]> {
    return this.httpClient
      .get<any>(this.url)
      .pipe(map((response) => this.extractItems<any>(response)));
  }

  public getCluster(projectId: string): Observable<any> {
    return this.httpClient.get<any>(`${this.url}/${projectId}`);
  }
}
